import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import {
  fetchRxntProvidersByClinic,
  searchMedications,
  setSelectedPharmacy,
  addSelectedMedication,
  removeSelectedMedication,
  updateSelectedMedication,
  clearPharmacySearch,
  clearMedicationSearch,
  clearSelectedMedications,
  fetchRxntPrescriptions,
} from '../../../../store/RxntSlice';
import FormInputs from '../../../../components/UI/FormInputs';
import SelectList from '../../../../components/UI/FormInputs/SelectList';
import TableGrid from '../../../../components/UI/TableGrid';
import Buttons from '../../../../components/UI/Buttons';
import RxntService from '../../../../services/RxntService';
import VisitsService from '../../../../services/VisitsService';
import ProviderService from '../../../../services/ProviderService';
import OrganizationService from '../../../../services/OrganizationService';
import EmployeeService from '../../../../services/EmployeeService';
import RxntPharmacyPicker from '../../../../components/Rxnt/RxntPharmacyPicker';
import {
  formatPharmacyAddress,
  getPharmacyFax,
  getPharmacyPhone,
  pharmacyFiltersFromSelectedPharmacy,
  selectedPharmacyFromEmployee,
  extractRxntPrescriptionErrorMessage,
} from '../../../../utils/rxntUiUtils';
import { OK, CREATED } from '../../../../utils/constant';
import PERMISSIONS from '../../../../utils/AclPermissions';
import {
  buildSigFromBuilder,
  defaultSigBuilderFromDrug,
  drugUnitSelectOptions,
  emptySigBuilder,
  frequencyUnitSelectOptions,
  hasSigBuilderOptions,
  stringListToSelectOptions,
} from '../../../../utils/rxntSigUtils';

const normalizeClinicId = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw.clinic_id ?? raw.id ?? null;
  return raw;
};

/** Human-readable clinic label from API shapes (object with name, or fallback id). */
const resolveClinicDisplayName = (rawClinic, fallbackId) => {
  if (typeof rawClinic === 'object' && rawClinic !== null) {
    const n =
      rawClinic.name ??
      rawClinic.clinic_name ??
      rawClinic.company_name ??
      rawClinic.label ??
      rawClinic.title;
    if (n != null && String(n).trim() !== '') return String(n).trim();
  }
  if (fallbackId != null && fallbackId !== '') return String(fallbackId);
  return '';
};

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const newClientRowId = () => `rxnt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** RXNT refills: non-negative integer, max two digits (0–99). */
const RXNT_MAX_REFILLS = 99;

function clampRefillsInput(val) {
  const digits = String(val ?? '').replace(/\D/g, '').slice(0, 2);
  if (digits === '') return '';
  const n = Math.min(RXNT_MAX_REFILLS, Math.max(0, parseInt(digits, 10) || 0));
  return String(n);
}

const RxntPrescriptionForm = ({
  visitId,
  employeeId,
  clinicId,
  providerId,
  visitDetails,
  onClose,
  onSubmitSuccess,
  Toast,
  Loader,
  fetchVisitList,
  embedded = false,
}) => {
  const ERROR_TOAST_TIMEOUT = 6000;
  const dispatch = useDispatch();
  const {
    providers,
    medicationSearch,
    selectedPharmacy,
    selectedMedications,
  } = useSelector((state) => state.rxnt);

  const pharmacyInitialFilters = useMemo(
    () => pharmacyFiltersFromSelectedPharmacy(selectedPharmacy),
    [selectedPharmacy]
  );

  const pharmacyFiltersSyncKey =
    selectedPharmacy?.pharmacyId ?? selectedPharmacy?.id ?? '';

  const [selectedProviderId, setSelectedProviderId] = useState(providerId || '');
  const [medicationSearchTerm, setMedicationSearchTerm] = useState('');
  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);
  const [currentMedication, setCurrentMedication] = useState({
    sig: '',
    quantity: '',
    quantity_unit: 'EA',
    days_supply: '30',
    refills: '0',
    is_use_generic: true,
    pharmacy_notes: '',
  });
  /** Drug chosen from search; user fills sig/qty then clicks Add to list. */
  const [stagedDrug, setStagedDrug] = useState(null);
  const [sigInputMode, setSigInputMode] = useState('free_text');
  const [drugDetails, setDrugDetails] = useState(null);
  const [drugDetailsLoading, setDrugDetailsLoading] = useState(false);
  const [sigBuilder, setSigBuilder] = useState({ ...emptySigBuilder });
  const [editingClientRowId, setEditingClientRowId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(clinicId || '');
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);
  const [showAssignProviderModal, setShowAssignProviderModal] = useState(false);
  const [assignModalProviders, setAssignModalProviders] = useState([]);
  const [assignModalSelected, setAssignModalSelected] = useState('');
  const [assignModalLoading, setAssignModalLoading] = useState(false);

  const visitProviderId = useMemo(() => {
    const v = visitDetails?.['0'] ?? visitDetails;
    const id = v?.primary_provider_id ?? v?.provider_id ?? providerId;
    if (id == null || id === '') return null;
    return id;
  }, [visitDetails, providerId]);

  /** Patient / employee home clinic (distinct from visit-assigned clinic on `clinicId`). */
  const normalizedEmployeeClinicId = useMemo(() => {
    const flat = visitDetails?.['0'] ?? visitDetails;
    const emp = flat?.employee ?? visitDetails?.employee;
    return normalizeClinicId(emp?.clinic_id ?? emp?.clinic);
  }, [visitDetails]);

  const medicationTableHeaders = useMemo(
    () => [
      { label: 'Medication', key: 'drug_name' },
      { label: 'Strength / form', key: 'strength_form' },
      { label: 'Qty', key: 'qty' },
      { label: 'Days', key: 'days' },
      { label: 'SIG', key: 'sig' },
      { label: 'Action', key: 'action', type: 'Actions' },
    ],
    []
  );

  const quantityUnitOptions = useMemo(
    () => [
      { label: 'Each', value: 'EA' },
      { label: 'mL', value: 'ML' },
      { label: 'Gram', value: 'GM' },
      { label: 'Tablet', value: 'TAB' },
      { label: 'Capsule', value: 'CAP' },
    ],
    []
  );

  const sigActionOptions = useMemo(
    () => stringListToSelectOptions(drugDetails?.actionOptions),
    [drugDetails]
  );

  const sigRouteOptions = useMemo(
    () => stringListToSelectOptions(drugDetails?.routeOptions),
    [drugDetails]
  );

  const sigUnitOptions = useMemo(
    () => drugUnitSelectOptions(drugDetails),
    [drugDetails]
  );

  const sigFrequencyOptions = useMemo(() => frequencyUnitSelectOptions(), []);

  const loadDrugDetails = useCallback(async (drugId, { metadataOnly = false } = {}) => {
    if (!drugId) {
      if (!metadataOnly) {
        setDrugDetails(null);
        setSigBuilder({ ...emptySigBuilder });
        setSigInputMode('free_text');
      }
      return;
    }

    setDrugDetailsLoading(true);
    if (!metadataOnly) {
      setDrugDetails(null);
    }
    try {
      const response = await RxntService.getDrugDetails(drugId);
      const data = response?.code === OK ? response.data : null;
      setDrugDetails(data);

      if (!metadataOnly && data && hasSigBuilderOptions(data)) {
        const builder = defaultSigBuilderFromDrug(data);
        setSigBuilder(builder);
        setSigInputMode('builder');
        setCurrentMedication((prev) => ({
          ...prev,
          sig: buildSigFromBuilder(builder),
        }));
      } else if (!metadataOnly) {
        setSigBuilder({ ...emptySigBuilder });
        setSigInputMode('free_text');
      }
    } catch (_) {
      if (!metadataOnly) {
        setDrugDetails(null);
        setSigBuilder({ ...emptySigBuilder });
        setSigInputMode('free_text');
      }
    } finally {
      setDrugDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sigInputMode !== 'builder' || !drugDetails) return;
    setCurrentMedication((prev) => ({
      ...prev,
      sig: buildSigFromBuilder(sigBuilder),
    }));
  }, [sigInputMode, sigBuilder, drugDetails]);

  const handleSigBuilderChange = (field, value) => {
    setSigBuilder((prev) => ({ ...prev, [field]: value }));
  };

  const handleSigInputModeChange = (mode) => {
    if (mode === 'builder' && drugDetails && hasSigBuilderOptions(drugDetails)) {
      setSigBuilder((prev) => {
        const hasValues = prev.action || prev.unit || prev.route;
        return hasValues ? prev : defaultSigBuilderFromDrug(drugDetails);
      });
    }
    setSigInputMode(mode);
  };

  useEffect(() => {
    loadMappedLocations();
    return () => {
      dispatch(clearSelectedMedications());
      dispatch(clearPharmacySearch());
      dispatch(clearMedicationSearch());
    };
  }, [dispatch]);

  useEffect(() => {
    if (providerId != null && providerId !== '') {
      setSelectedProviderId(providerId);
    }
  }, [providerId]);

  useEffect(() => {
    let cancelled = false;

    const applyPreferredPharmacy = (employee) => {
      const preferred = selectedPharmacyFromEmployee(employee);
      if (preferred && !cancelled) {
        dispatch(setSelectedPharmacy(preferred));
      }
    };

    const flat = visitDetails?.['0'] ?? visitDetails;
    const empFromVisit = flat?.employee ?? visitDetails?.employee;
    if (empFromVisit?.preferred_rxnt_pharmacy_id) {
      applyPreferredPharmacy(empFromVisit);
      return () => {
        cancelled = true;
      };
    }

    if (!employeeId) {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const employee = await EmployeeService.getEmployee(employeeId);
        if (!cancelled && employee) {
          applyPreferredPharmacy(employee);
        }
      } catch (_) {
        /* optional pre-fill */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [employeeId, visitDetails, dispatch]);

  useEffect(() => {
    if (providers.loading || visitProviderId == null || providers.data.length === 0) {
      return;
    }
    const match = providers.data.find(
      (p) => String(p.provider_id) === String(visitProviderId)
    );
    if (match) {
      setSelectedProviderId(match.provider_id);
    }
  }, [providers.loading, providers.data, visitProviderId]);

  const loadMappedLocations = async () => {
    setIsLoadingClinics(true);
    try {
      const response = await RxntService.getMappedLocations();
      if (response.code === OK) {
        const clinicList = (response.data || []).map((m) => ({
          clinic_id: m.clinic_id,
          name: m.clinic?.name || m.rxnt_location_name,
        }));
        setClinics(clinicList);

        const flat = visitDetails?.['0'] ?? visitDetails;
        const emp = flat?.employee ?? visitDetails?.employee;
        const employeeClinic = normalizeClinicId(emp?.clinic_id ?? emp?.clinic);
        const visitClinic = normalizeClinicId(clinicId);

        const isMapped = (id) =>
          id && clinicList.some((c) => String(c.clinic_id) === String(id));

        const visitClinicMapped = isMapped(visitClinic);
        const employeeClinicMapped = isMapped(employeeClinic);

        if (visitClinicMapped) {
          setSelectedClinicId(visitClinic);
          dispatch(fetchRxntProvidersByClinic(visitClinic));
        } else if (employeeClinicMapped) {
          setSelectedClinicId(employeeClinic);
          dispatch(fetchRxntProvidersByClinic(employeeClinic));
        } else if (clinicList.length > 0 && !visitClinic && !employeeClinic) {
          setSelectedClinicId(clinicList[0].clinic_id);
          dispatch(fetchRxntProvidersByClinic(clinicList[0].clinic_id));
        } else {
          setSelectedClinicId('');
        }
      }
    } catch (error) {
      Toast.error('Failed to load clinic mappings', ERROR_TOAST_TIMEOUT);
    } finally {
      setIsLoadingClinics(false);
    }
  };

  const normalizedVisitClinicId = normalizeClinicId(clinicId);

  const employeeClinicDisplayName = useMemo(() => {
    const flat = visitDetails?.['0'] ?? visitDetails;
    const emp = flat?.employee ?? visitDetails?.employee;
    const raw = emp?.clinic_id ?? emp?.clinic;
    return resolveClinicDisplayName(
      typeof raw === 'object' ? raw : emp?.clinic,
      normalizedEmployeeClinicId
    );
  }, [visitDetails, normalizedEmployeeClinicId]);

  const visitClinicDisplayName = useMemo(() => {
    const flat = visitDetails?.['0'] ?? visitDetails;
    const raw =
      flat?.assigned_clinic ??
      visitDetails?.assigned_clinic ??
      flat?.assigned_clinic_id ??
      visitDetails?.assigned_clinic_id;
    return resolveClinicDisplayName(
      typeof raw === 'object' ? raw : null,
      normalizedVisitClinicId
    );
  }, [visitDetails, normalizedVisitClinicId]);

  const employeeClinicLinkedToRxnt =
    !normalizedEmployeeClinicId ||
    clinics.some((c) => String(c.clinic_id) === String(normalizedEmployeeClinicId));

  const visitClinicLinkedToRxnt =
    !normalizedVisitClinicId ||
    clinics.some((c) => String(c.clinic_id) === String(normalizedVisitClinicId));

  /** Prefer checking employee clinic when present; otherwise require visit clinic to be mapped. */
  const assignedClinicRxntPolicyOk =
    normalizedEmployeeClinicId
      ? employeeClinicLinkedToRxnt
      : !normalizedVisitClinicId || visitClinicLinkedToRxnt;

  const showClinicAdminMessage =
    !isLoadingClinics &&
    (clinics.length === 0 || (!assignedClinicRxntPolicyOk && !selectedClinicId));

  const clinicAdminMessageText = showClinicAdminMessage
    ? clinics.length === 0
      ? 'No clinics mapped to RXNT locations. Contact admin.'
      : normalizedEmployeeClinicId && !employeeClinicLinkedToRxnt
        ? `${employeeClinicDisplayName || 'This clinic'} is not linked to RXNT. Contact admin.`
        : !normalizedEmployeeClinicId &&
            normalizedVisitClinicId &&
            !visitClinicLinkedToRxnt
          ? `${visitClinicDisplayName || 'This clinic'} is not linked to RXNT. Contact admin.`
          : 'No clinics mapped to RXNT locations. Contact admin.'
    : '';

  const visitHasProvider =
    visitProviderId != null && visitProviderId !== '';

  const providerListedForRxnt =
    visitHasProvider &&
    providers.data.some((p) => String(p.provider_id) === String(visitProviderId));

  const showAssignVisitProvider =
    Boolean(selectedClinicId) && !visitHasProvider && !providers.loading;

  const showProviderNotMappedRxnt =
    Boolean(selectedClinicId) &&
    visitHasProvider &&
    !providers.loading &&
    !providerListedForRxnt;

  const clinicIdForAssignModal =
    selectedClinicId ||
    normalizedVisitClinicId ||
    normalizedEmployeeClinicId ||
    normalizeClinicId(visitDetails?.['0']?.assigned_clinic ?? visitDetails?.assigned_clinic) ||
    visitDetails?.['0']?.clinic_id;

  const openAssignProviderModal = async () => {
    if (!clinicIdForAssignModal) {
      Toast.error(
        'Clinic is required to assign a provider. Select a mapped clinic or update the visit.',
        ERROR_TOAST_TIMEOUT
      );
      return;
    }
    setShowAssignProviderModal(true);
    setAssignModalSelected('');
    setAssignModalLoading(true);
    try {
      const data = await ProviderService.getProviderByClinicId(
        OrganizationService.getOrgId(),
        clinicIdForAssignModal
      );
      if (data && Array.isArray(data)) {
        const options = [{ label: '-Select Provider-', value: '' }];
        data.forEach((provider) => {
          options.push({
            label: `${provider.first_name || ''} ${provider.last_name || ''}`.trim(),
            value: provider.provider_id,
          });
        });
        setAssignModalProviders(options);
      }
    } catch (err) {
      console.error(err);
      Toast.error('Failed to load providers', ERROR_TOAST_TIMEOUT);
    } finally {
      setAssignModalLoading(false);
    }
  };

  const handleSaveAssignProvider = async () => {
    if (!assignModalSelected) {
      Toast.error('Please select a provider', ERROR_TOAST_TIMEOUT);
      return;
    }
    setAssignModalLoading(true);
    try {
      const response = await VisitsService.updateProvider(visitId, {
        primary_provider_id: assignModalSelected,
      });
      if (
        response &&
        (response.code === OK || response.code === 200 || response.code === 'OK')
      ) {
        Toast.success('Provider assigned successfully');
        setShowAssignProviderModal(false);
        setAssignModalSelected('');
        if (typeof fetchVisitList === 'function') {
          await fetchVisitList();
        }
        setSelectedProviderId(assignModalSelected);
        if (selectedClinicId) {
          dispatch(fetchRxntProvidersByClinic(selectedClinicId));
        }
      } else {
        Toast.error(
          response?.data?.error || response?.data || 'Failed to assign provider',
          ERROR_TOAST_TIMEOUT
        );
      }
    } catch (error) {
      Toast.error(error.message || 'Failed to assign provider', ERROR_TOAST_TIMEOUT);
    } finally {
      setAssignModalLoading(false);
    }
  };

  const handleCancelAssignProvider = () => {
    setShowAssignProviderModal(false);
    setAssignModalSelected('');
  };

  const handleClinicChange = (e) => {
    const newClinicId = e.target.value;
    setSelectedClinicId(newClinicId);
    setSelectedProviderId('');
    if (newClinicId) {
      dispatch(fetchRxntProvidersByClinic(newClinicId));
    }
  };

  const handleProviderChange = (e) => {
    setSelectedProviderId(e.target.value);
  };

  const debouncedMedicationSearch = useMemo(
    () => debounce((term) => {
      if (term.trim().length >= 1) {
        dispatch(searchMedications({ search: term.trim(), page: 1 }));
      }
    }, 300),
    [dispatch]
  );

  const handleMedicationDropdownScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 72;
      if (!nearBottom) return;
      if (
        !medicationSearch.hasNext ||
        medicationSearch.loadingMore ||
        medicationSearch.loading
      ) {
        return;
      }
      const term = medicationSearchTerm.trim();
      if (term.length < 1) return;
      dispatch(
        searchMedications({
          search: term,
          page: (medicationSearch.page || 1) + 1,
          append: true,
        })
      );
    },
    [
      medicationSearch.hasNext,
      medicationSearch.loadingMore,
      medicationSearch.loading,
      medicationSearch.page,
      medicationSearchTerm,
      dispatch,
    ]
  );

  const handleMedicationSearch = (e) => {
    const term = e.target.value;
    setMedicationSearchTerm(term);
    setShowMedicationDropdown(true);
    debouncedMedicationSearch(term);
  };

  const handleMedicationSelect = (medication) => {
    const drugId = medication.drugId || medication.id;
    setStagedDrug({
      drug_id: drugId,
      drug_name: medication.drugName || medication.name,
      drug_strength: medication.strength || medication.drugStrength || '',
      drug_form: medication.form || medication.drugForm || '',
      ndc: medication.ndc || '',
    });
    setMedicationSearchTerm('');
    setShowMedicationDropdown(false);
    dispatch(clearMedicationSearch());
    loadDrugDetails(drugId);
  };

  const resetStagingFields = useCallback(() => {
    setCurrentMedication({
      sig: '',
      quantity: '',
      quantity_unit: 'EA',
      days_supply: '30',
      refills: '0',
      is_use_generic: true,
      pharmacy_notes: '',
    });
    setStagedDrug(null);
    setSigInputMode('free_text');
    setDrugDetails(null);
    setDrugDetailsLoading(false);
    setSigBuilder({ ...emptySigBuilder });
    setEditingClientRowId(null);
  }, []);

  const handleAddMedicationToList = () => {
    if (!stagedDrug) {
      Toast.error('Search and select a medication first', ERROR_TOAST_TIMEOUT);
      return;
    }
    if (!currentMedication.sig || !currentMedication.sig.trim()) {
      Toast.error('SIG (instructions) is required', ERROR_TOAST_TIMEOUT);
      return;
    }
    if (!currentMedication.quantity || Number(currentMedication.quantity) <= 0) {
      Toast.error('Enter a valid quantity', ERROR_TOAST_TIMEOUT);
      return;
    }

    const row = {
      clientRowId: editingClientRowId || newClientRowId(),
      drug_id: stagedDrug.drug_id,
      drug_name: stagedDrug.drug_name,
      drug_strength: stagedDrug.drug_strength,
      drug_form: stagedDrug.drug_form,
      ndc: stagedDrug.ndc || '',
      sig: currentMedication.sig.trim(),
      quantity: currentMedication.quantity,
      quantity_unit: currentMedication.quantity_unit,
      days_supply: currentMedication.days_supply,
      refills: currentMedication.refills,
      is_use_generic: Boolean(currentMedication.is_use_generic),
      pharmacy_notes: currentMedication.pharmacy_notes || '',
      sig_builder_action:
        sigInputMode === 'builder' && sigBuilder.action ? sigBuilder.action : '',
    };

    if (editingClientRowId) {
      const idx = selectedMedications.findIndex((m) => m.clientRowId === editingClientRowId);
      if (idx === -1) {
        Toast.error('Could not update row', ERROR_TOAST_TIMEOUT);
        return;
      }
      dispatch(updateSelectedMedication({ index: idx, data: { ...row, clientRowId: editingClientRowId } }));
    } else {
      dispatch(addSelectedMedication(row));
    }
    resetStagingFields();
  };

  const handleEditMedicationRow = (med) => {
    setStagedDrug({
      drug_id: med.drug_id,
      drug_name: med.drug_name,
      drug_strength: med.drug_strength,
      drug_form: med.drug_form,
      ndc: med.ndc || '',
    });
    setCurrentMedication({
      sig: med.sig || '',
      quantity: med.quantity != null ? String(med.quantity) : '',
      quantity_unit: med.quantity_unit || 'EA',
      days_supply: med.days_supply != null ? String(med.days_supply) : '30',
      refills: clampRefillsInput(med.refills != null ? String(med.refills) : '0') || '0',
      is_use_generic: med.is_use_generic !== false,
      pharmacy_notes: med.pharmacy_notes || '',
    });
    setSigInputMode('free_text');
    setSigBuilder({ ...emptySigBuilder });
    setDrugDetails(null);
    setDrugDetailsLoading(false);
    if (med.drug_id) {
      loadDrugDetails(med.drug_id, { metadataOnly: true });
    }
    setEditingClientRowId(med.clientRowId);
    setMedicationSearchTerm('');
    setShowMedicationDropdown(false);
  };

  const handleRemoveMedicationRow = (med) => {
    const idx = selectedMedications.findIndex((m) => m.clientRowId === med.clientRowId);
    if (idx === -1) return;
    if (editingClientRowId === med.clientRowId) {
      resetStagingFields();
    }
    dispatch(removeSelectedMedication(idx));
  };

  const medicationTableRecords = useMemo(() => {
    const tableData = selectedMedications.map((med) => ({
      rowId: med.clientRowId,
      data: {
        drug_name: {
          value: (
            <span className="forTable_textEllipsis" title={med.drug_name || '—'}>
              {med.drug_name || '—'}
            </span>
          ),
        },
        strength_form: {
          value: (
            <span
              className="forTable_textEllipsis"
              title={`${med.drug_strength || ''} ${med.drug_form || ''}`.trim() || '—'}
            >
              {`${med.drug_strength || ''} ${med.drug_form || ''}`.trim() || '—'}
            </span>
          ),
        },
        qty: {
          value: (
            <span className="forTable_textEllipsis" title={`${med.quantity} ${med.quantity_unit || ''}`.trim()}>
              {`${med.quantity} ${med.quantity_unit || ''}`.trim()}
            </span>
          ),
        },
        days: {
          value: (
            <span className="forTable_textEllipsis" title={String(med.days_supply ?? '—')}>
              {med.days_supply ?? '—'}
            </span>
          ),
        },
        sig: {
          value: (
            <span className="forTable_textEllipsis" title={med.sig || '—'}>
              {med.sig || '—'}
            </span>
          ),
        },
        action: [
          {
            className: 'btn btn-icon btn-transparent-dark',
            iconType: 'Edit',
            title: 'Edit',
            acl: PERMISSIONS.VISITS.EDIT,
            clickHandler: (e) => {
              e?.stopPropagation?.();
              handleEditMedicationRow(med);
            },
          },
          {
            className: 'btn btn-icon btn-transparent-dark',
            iconType: 'Remove',
            title: 'Remove',
            isDelete: true,
            acl: PERMISSIONS.VISITS.DELETE,
            clickHandler: (e) => {
              e?.stopPropagation?.();
              handleRemoveMedicationRow(med);
            },
          },
        ],
      },
    }));
    return {
      tableHeaders: medicationTableHeaders,
      tableRows: { data: tableData, status: 200 },
      pagination: {
        page: 1,
        perPage: 25,
        totalRecords: tableData.length,
      },
    };
  }, [selectedMedications, medicationTableHeaders]);

  const validateForm = () => {
    if (!selectedClinicId) {
      Toast.error('Please select a clinic', ERROR_TOAST_TIMEOUT);
      return false;
    }
    if (!selectedProviderId) {
      Toast.error('Please select a provider', ERROR_TOAST_TIMEOUT);
      return false;
    }
    if (!selectedPharmacy) {
      Toast.error('Please select a pharmacy', ERROR_TOAST_TIMEOUT);
      return false;
    }
    if (selectedMedications.length === 0) {
      Toast.error('Please add at least one medication', ERROR_TOAST_TIMEOUT);
      return false;
    }
    
    for (let i = 0; i < selectedMedications.length; i++) {
      const med = selectedMedications[i];
      if (!med.sig) {
        Toast.error(`Please enter sig instructions for ${med.drug_name}`, ERROR_TOAST_TIMEOUT);
        return false;
      }
      if (!med.quantity || med.quantity <= 0) {
        Toast.error(`Please enter a valid quantity for ${med.drug_name}`, ERROR_TOAST_TIMEOUT);
        return false;
      }
    }
    
    return true;
  };

  const buildPrescriptionPayload = (medication) => {
    const { clientRowId: _c, ...med } = medication;
    return {
      visit_id: visitId,
      employee_id: employeeId,
      provider_id: selectedProviderId,
      clinic_id: selectedClinicId,
      pharmacy_id: selectedPharmacy.pharmacyId || selectedPharmacy.id,
      pharmacy_name: selectedPharmacy.name || selectedPharmacy.pharmacyName,
      pharmacy_address: formatPharmacyAddress(selectedPharmacy),
      pharmacy_phone: getPharmacyPhone(selectedPharmacy),
      pharmacy_fax: getPharmacyFax(selectedPharmacy),
      pharmacy_delivery_methods: selectedPharmacy.deliveryMethods || 1,
      drug_id: med.drug_id,
      drug_name: med.drug_name,
      drug_strength: med.drug_strength,
      drug_form: med.drug_form,
      ndc: med.ndc,
      sig: med.sig,
      quantity: parseInt(med.quantity, 10),
      quantity_unit: med.quantity_unit,
      days_supply: med.days_supply ? parseInt(med.days_supply, 10) : null,
      refills: Math.min(
        RXNT_MAX_REFILLS,
        Math.max(0, med.refills ? parseInt(med.refills, 10) : 0) || 0
      ),
      is_use_generic: med.is_use_generic !== false,
      pharmacy_notes: (med.sig || '').trim(),
      sig_builder_action: med.sig_builder_action || '',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const rxntProvider = providers.data.find(
      (p) => String(p.provider_id) === String(selectedProviderId)
    );
    if (!rxntProvider) {
      Toast.error('Selected provider is not mapped to RXNT', ERROR_TOAST_TIMEOUT);
      return;
    }

    setIsSubmitting(true);
    if (Loader) Loader(true);
    const sendCount = selectedMedications.length;
    try {
      for (let i = 0; i < selectedMedications.length; i += 1) {
        const medication = selectedMedications[i];
        const prescriptionData = buildPrescriptionPayload(medication);
        const res = await RxntService.createPrescription(prescriptionData);
        const code = res?.code;
        if (code !== OK && code !== CREATED) {
          const drugLabel = medication.drug_name || 'medication';
          Toast.error(
            extractRxntPrescriptionErrorMessage(res, null, drugLabel),
            ERROR_TOAST_TIMEOUT
          );
          return;
        }
      }

      dispatch(clearSelectedMedications());
      dispatch(clearPharmacySearch());
      dispatch(clearMedicationSearch());
      resetStagingFields();
      await dispatch(fetchRxntPrescriptions(visitId));
      if (onSubmitSuccess) {
        onSubmitSuccess({ count: sendCount });
      } else {
        Toast.success(
          sendCount === 1
            ? 'Prescription sent successfully'
            : `${sendCount} prescriptions sent successfully`,
          ERROR_TOAST_TIMEOUT
        );
      }
    } catch (error) {
      Toast.error(
        extractRxntPrescriptionErrorMessage(null, error),
        ERROR_TOAST_TIMEOUT
      );
    } finally {
      setIsSubmitting(false);
      if (Loader) Loader(false);
    }
  };

  return (
    <div className={`rxnt-form-container${embedded ? ' rxnt-form-container-embedded' : ''}`}>
      {!embedded && (
        <div className="rxnt-form-header">
          <h2>New ePrescription</h2>
          <button className="rxnt-btn rxnt-btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={embedded ? 'rxnt-form rxnt-form--embedded' : 'rxnt-form'}
      >
        <div className={embedded ? 'rxnt-form-scroll-pane' : 'rxnt-form-page-flow'}>
        <div className="card mb-3 rxnt-form-section-card">
          <div className="card-header d-flex justify-content-between align-items-center p-2">
            <h6 className="mb-0 text-md">Clinic & Provider</h6>
          </div>
          <div className="card-body">
          <div className="rxnt-form-row">
            <div className="rxnt-form-group">
              <label>Clinic *</label>
              <select
                value={selectedClinicId}
                onChange={handleClinicChange}
                disabled={isLoadingClinics}
              >
                <option value="">Select Clinic</option>
                {clinics.map((clinic) => (
                  <option key={clinic.clinic_id} value={clinic.clinic_id}>
                    {clinic.name}
                  </option>
                ))}
              </select>
              {showClinicAdminMessage && clinicAdminMessageText && (
                <span className="rxnt-field-hint rxnt-warning">
                  {clinicAdminMessageText}
                </span>
              )}
            </div>
            <div className="rxnt-form-group">
              <label>Provider *</label>
              <select
                value={selectedProviderId}
                onChange={handleProviderChange}
                disabled={
                  !selectedClinicId ||
                  providers.loading ||
                  showAssignVisitProvider ||
                  showProviderNotMappedRxnt
                }
              >
                <option value="">Select Provider</option>
                {providers.data.map((p) => (
                  <option key={p.provider_id} value={p.provider_id}>
                    {p.provider?.first_name} {p.provider?.last_name}
                  </option>
                ))}
              </select>
              {providers.loading && <span className="rxnt-field-hint">Loading providers...</span>}
              {showAssignVisitProvider && (
                <div className="mt-1">
                  <span className="rxnt-field-hint rxnt-warning d-block mb-1">
                    Assign a provider to this visit before e-prescribing.
                  </span>
                  <Buttons
                    className="btn btn-sm btn-primary"
                    label="Assign provider"
                    acl="any"
                    clickHandler={openAssignProviderModal}
                  />
                </div>
              )}
              {showProviderNotMappedRxnt && (
                <span className="rxnt-field-hint rxnt-warning">
                  This visit&apos;s provider is not mapped to RXNT. Contact admin.
                </span>
              )}
              {!showAssignVisitProvider &&
                !showProviderNotMappedRxnt &&
                !providers.loading &&
                selectedClinicId &&
                providers.data.length === 0 && (
                  <span className="rxnt-field-hint rxnt-warning">
                    No providers mapped for this clinic.
                  </span>
                )}
            </div>
          </div>
          </div>
        </div>

        <RxntPharmacyPicker
          selectedPharmacy={selectedPharmacy}
          initialFilters={pharmacyInitialFilters}
          filtersSyncKey={pharmacyFiltersSyncKey}
          onSelect={(pharmacy) => dispatch(setSelectedPharmacy(pharmacy))}
          onClear={() => dispatch(setSelectedPharmacy(null))}
          showRequiredBadge
          Toast={Toast}
        />

        <div className="row">
          <div className="col-md-4">
            <div className="card mb-3" style={{ border: '1px solid #e0e0e0' }}>
              <div className="card-header d-flex justify-content-between align-items-center p-2">
                <h6 className="mb-0 text-md">Add medication</h6>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <h6 className="text-sm font-weight-bold mb-2 text-uppercase text-muted" style={{ letterSpacing: '0.02em' }}>
                    Search medication
                  </h6>
                  <div className="form-group mb-0">
                    <label className="mb-1 text-sm opacity-75 d-block" htmlFor="rxnt_medication_search">
                      Drug name (RXNT) <span className="text-danger">*</span>
                    </label>
                    <div className="rxnt-search-container">
                      <input
                        id="rxnt_medication_search"
                        type="text"
                        className="form-control form-control-solid"
                        value={medicationSearchTerm}
                        onChange={handleMedicationSearch}
                        onFocus={() => setShowMedicationDropdown(true)}
                        placeholder="Enter drug name"
                        autoComplete="off"
                      />
                      {showMedicationDropdown && medicationSearchTerm.trim().length >= 1 && (
                        <div
                          className="rxnt-search-dropdown"
                          onScroll={handleMedicationDropdownScroll}
                        >
                          {medicationSearch.error ? (
                            <div className="rxnt-dropdown-loading rxnt-dropdown-error">
                              {medicationSearch.error}
                            </div>
                          ) : medicationSearch.loading && !medicationSearch.loadingMore ? (
                            <div className="rxnt-dropdown-loading">Searching…</div>
                          ) : medicationSearch.results.length === 0 ? (
                            <div className="rxnt-dropdown-loading">No medications found</div>
                          ) : (
                            <>
                              {medicationSearch.results.map((drug, idx) => (
                                <div
                                  key={drug.drugId || drug.id || idx}
                                  className="rxnt-dropdown-item"
                                  onClick={() => handleMedicationSelect(drug)}
                                >
                                  <div className="rxnt-dropdown-item-main">
                                    {drug.drugName || drug.name}
                                  </div>
                                  <div className="rxnt-dropdown-item-sub">
                                    {drug.strength && `${drug.strength} `}
                                    {drug.form && `| ${drug.form}`}
                                  </div>
                                </div>
                              ))}
                              {medicationSearch.loadingMore && (
                                <div className="rxnt-dropdown-loading">Loading more…</div>
                              )}
                              {medicationSearch.hasNext && !medicationSearch.loadingMore && (
                                <div className="rxnt-dropdown-hint">
                                  Scroll for more ({medicationSearch.results.length}
                                  {medicationSearch.totalCount
                                    ? ` of ${medicationSearch.totalCount.toLocaleString()}`
                                    : ''}
                                  )
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <small className="text-muted d-block mt-1">Results come from the RXNT drug list.</small>
                  </div>
                </div>

                {stagedDrug && (
                  <div className="mb-3 p-2 rounded border" style={{ background: 'var(--light-base-bg, #f5f5f5)' }}>
                    <h6 className="text-sm font-weight-bold mb-2">Selected drug</h6>
                    <div className="font-weight-bold">{stagedDrug.drug_name}</div>
                    <div className="text-muted small">
                      {[stagedDrug.drug_strength, stagedDrug.drug_form].filter(Boolean).join(' · ')}
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 mt-1"
                      onClick={() => {
                        setStagedDrug(null);
                        setDrugDetails(null);
                        setDrugDetailsLoading(false);
                        setSigBuilder({ ...emptySigBuilder });
                        setSigInputMode('free_text');
                        setCurrentMedication((prev) => ({ ...prev, sig: '' }));
                      }}
                    >
                      Clear selection
                    </button>
                  </div>
                )}

                <div className="pt-2 border-top">
                  <h6 className="text-sm font-weight-bold mb-3 text-uppercase text-muted" style={{ letterSpacing: '0.02em' }}>
                    Dosing &amp; instructions
                  </h6>
                  <div className="form-row">
                    <div className="form-group col-12 col-sm-6">
                      <label className="mb-1 text-sm opacity-75 d-block" htmlFor="rxnt_qty">
                        Quantity <span className="text-danger">*</span>
                      </label>
                      <FormInputs
                        type="Number"
                        name="rxnt_qty"
                        id="rxnt_qty"
                        className="form-control form-control-solid"
                        value={currentMedication.quantity}
                        changeHandler={(val) => setCurrentMedication((prev) => ({ ...prev, quantity: val }))}
                        placeholder="e.g. 30"
                      />
                    </div>
                    <div className="form-group col-12 col-sm-6">
                      <label className="mb-1 text-sm opacity-75 d-block" htmlFor="rxnt_qty_unit">
                        Unit
                      </label>
                      <FormInputs
                        type="SelectList"
                        name="rxnt_qty_unit"
                        id="rxnt_qty_unit"
                        className="form-control form-control-solid"
                        options={quantityUnitOptions}
                        value={currentMedication.quantity_unit}
                        changeHandler={(val) => setCurrentMedication((prev) => ({ ...prev, quantity_unit: val || 'EA' }))}
                        required={false}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group col-12 col-sm-6">
                      <label className="mb-1 text-sm opacity-75 d-block" htmlFor="rxnt_days">
                        Days supply
                      </label>
                      <FormInputs
                        type="Number"
                        name="rxnt_days"
                        id="rxnt_days"
                        className="form-control form-control-solid"
                        value={currentMedication.days_supply}
                        changeHandler={(val) => setCurrentMedication((prev) => ({ ...prev, days_supply: val }))}
                        placeholder="e.g. 30"
                      />
                    </div>
                    <div className="form-group col-12 col-sm-6">
                      <label className="mb-1 text-sm opacity-75 d-block" htmlFor="rxnt_refills">
                        Refills
                      </label>
                      <FormInputs
                        type="Number"
                        name="rxnt_refills"
                        id="rxnt_refills"
                        className="form-control form-control-solid"
                        value={currentMedication.refills}
                        max={2}
                        changeHandler={(val) =>
                          setCurrentMedication((prev) => ({
                            ...prev,
                            refills: clampRefillsInput(val),
                          }))
                        }
                        placeholder="0–99"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group col-12">
                      <div className="custom-control custom-switch mt-1">
                        <input
                          id="rxnt_is_use_generic"
                          type="checkbox"
                          className="custom-control-input"
                          checked={Boolean(currentMedication.is_use_generic)}
                          onChange={(e) =>
                            setCurrentMedication((prev) => ({
                              ...prev,
                              is_use_generic: e.target.checked,
                            }))
                          }
                        />
                        <label className="custom-control-label text-sm opacity-75" htmlFor="rxnt_is_use_generic">
                          Use generic
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="form-group mb-0">
                    <label className="mb-1 text-sm opacity-75 d-block">
                      Instructions <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
                      <FormInputs
                        type="RadioGroup"
                        id="rxnt_sig_mode"
                        name="rxnt_sig_mode"
                        value={sigInputMode}
                        options={[
                          { label: 'Free Text', value: 'free_text' },
                          {
                            label: 'Sig Builder',
                            value: 'builder',
                            className: `${
                              !stagedDrug || !hasSigBuilderOptions(drugDetails)
                                ? 'form-check form-check-inline d-inline-flex flex-shrink-0 opacity-50 pe-none'
                                : 'form-check form-check-inline d-inline-flex flex-shrink-0'
                            }`,
                            appendClassLabel: 'font-weight-normal',
                          },
                        ]}
                        changeHandler={(val) => {
                          if (
                            val === 'builder' &&
                            (!stagedDrug || !hasSigBuilderOptions(drugDetails))
                          ) {
                            return;
                          }
                          handleSigInputModeChange(val);
                        }}
                      />
                      {drugDetailsLoading && (
                        <span className="text-muted small">Loading drug options…</span>
                      )}
                    </div>

                    {sigInputMode === 'builder' && hasSigBuilderOptions(drugDetails) ? (
                      <div className="rxnt-sig-builder-panel border rounded p-2 bg-white">
                        <div className="form-row mb-2">
                          <div className="form-group col-12 col-sm-6 mb-0">
                            <FormInputs
                              type="SelectList"
                              name="rxnt_sig_action"
                              id="rxnt_sig_action"
                              className="form-control form-control-solid"
                              options={sigActionOptions}
                              value={sigBuilder.action}
                              changeHandler={(val) => handleSigBuilderChange('action', val)}
                              required={false}
                              placeholder="Action"
                            />
                          </div>
                          <div className="form-group col-12 col-sm-6 mb-0">
                            <input
                              type="text"
                              id="rxnt_sig_dose_qty"
                              className="form-control form-control-solid"
                              value={sigBuilder.doseQty}
                              onChange={(e) => handleSigBuilderChange('doseQty', e.target.value)}
                              inputMode="decimal"
                              placeholder="Qty"
                            />
                          </div>
                        </div>
                        <div className="form-row mb-2">
                          <div className="form-group col-12 col-sm-6 mb-0">
                            <FormInputs
                              type="SelectList"
                              name="rxnt_sig_unit"
                              id="rxnt_sig_unit"
                              className="form-control form-control-solid"
                              options={sigUnitOptions}
                              value={sigBuilder.unit}
                              changeHandler={(val) => handleSigBuilderChange('unit', val)}
                              required={false}
                              placeholder="Unit"
                            />
                          </div>
                          <div className="form-group col-12 col-sm-6 mb-0">
                            <FormInputs
                              type="SelectList"
                              name="rxnt_sig_route"
                              id="rxnt_sig_route"
                              className="form-control form-control-solid"
                              options={sigRouteOptions}
                              value={sigBuilder.route}
                              changeHandler={(val) => handleSigBuilderChange('route', val)}
                              required={false}
                              placeholder="Route"
                            />
                          </div>
                        </div>
                        <div className="form-row mb-0">
                          <div className="form-group col-12 col-sm-6 mb-0">
                            <input
                              type="text"
                              id="rxnt_sig_freq_qty"
                              className="form-control form-control-solid"
                              value={sigBuilder.frequencyQty}
                              onChange={(e) => handleSigBuilderChange('frequencyQty', e.target.value)}
                              inputMode="decimal"
                              placeholder="Qty"
                            />
                          </div>
                          <div className="form-group col-12 col-sm-6 mb-0">
                            <FormInputs
                              type="SelectList"
                              name="rxnt_sig_freq_unit"
                              id="rxnt_sig_freq_unit"
                              className="form-control form-control-solid"
                              options={sigFrequencyOptions}
                              value={sigBuilder.frequencyUnit}
                              changeHandler={(val) => handleSigBuilderChange('frequencyUnit', val)}
                              required={false}
                              placeholder="Frequency"
                            />
                          </div>
                        </div>
                        {currentMedication.sig && (
                          <div className="small text-muted mt-2 pt-2 border-top">
                            <span className="font-weight-bold">Preview: </span>
                            {currentMedication.sig}
                          </div>
                        )}
                      </div>
                    ) : (
                      <FormInputs
                        type="TextArea"
                        name="rxnt_sig"
                        id="rxnt_sig_ta"
                        className="form-control form-control-solid"
                        value={currentMedication.sig}
                        changeHandler={(val) => setCurrentMedication((prev) => ({ ...prev, sig: val }))}
                        placeholder="e.g. Take 1 tablet by mouth daily"
                        rows="3"
                      />
                    )}
                  </div>
                </div>

                {/* Pharmacy notes UI hidden — SIG is sent as RXNT prescription comments (see buildPrescriptionPayload). */}
                {/*
                <div className="form-group mb-0 pt-2">
                  <h6 className="text-sm font-weight-bold mb-2 text-uppercase text-muted" style={{ letterSpacing: '0.02em' }}>
                    Pharmacy notes
                  </h6>
                  <label className="mb-1 text-sm opacity-75 d-block" htmlFor="rxnt_pharm_notes_ta">
                    Notes for pharmacist <span className="text-muted font-weight-normal">(optional)</span>
                  </label>
                  <FormInputs
                    type="TextArea"
                    name="rxnt_pharm_notes"
                    id="rxnt_pharm_notes_ta"
                    className="form-control form-control-solid"
                    value={currentMedication.pharmacy_notes}
                    changeHandler={(val) => setCurrentMedication((prev) => ({ ...prev, pharmacy_notes: val }))}
                    placeholder="Optional notes to the pharmacy"
                    rows="2"
                  />
                </div>
                */}

                <div className="d-flex justify-content-center mt-3 pt-2 border-top">
                  <Buttons
                    className="btn btn-primary sh_btnprimary"
                    label={editingClientRowId ? 'Update line' : 'Add to list'}
                    clickHandler={handleAddMedicationToList}
                    acl={PERMISSIONS.VISITS.CREATE}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-8">
            <div className="card mb-3" style={{ border: '1px solid #e0e0e0' }}>
              <div className="card-header p-2">
                <h6 className="mb-0 text-md">Medications in this order</h6>
                {/* <small className="text-muted d-block">Send all at once with “Send all prescriptions” below.</small> */}
              </div>
              <div className="card-body p-0">
                <div style={{ maxHeight: 280, overflow: 'auto' }}>
                  {selectedMedications.length === 0 ? (
                    <div className="p-4 text-center text-muted small">
                      Add one or more medications from the left. Each line is sent to RXNT as its own
                      e-prescription.
                    </div>
                  ) : (
                    <TableGrid
                      hasPagination={false}
                      needCardBody={false}
                      gridEditProps={{ activeEditId: null, onChangeFormDataInEdit: () => {} }}
                      {...medicationTableRecords}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>

        <div
          className={
            embedded
              ? 'rxnt-form-modal-footer-bar d-flex justify-content-center flex-wrap gap-2'
              : 'd-flex justify-content-center mt-2 mb-0 flex-wrap'
          }
          style={embedded ? undefined : { gap: '8px' }}
        >
          <button type="button" className="btn btn-secondary sh_btnsecondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary sh_btnprimary"
            disabled={isSubmitting || selectedMedications.length === 0}
          >
            {isSubmitting ? 'Sending…' : 'Send all prescriptions'}
          </button>
        </div>
      </form>

      <Modal
        show={showAssignProviderModal}
        onHide={handleCancelAssignProvider}
        centered
        backdrop="static"
        className="sh_customModal"
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="text-lg mb-0 font-weight-bold">Assign provider to visit</h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Select provider</label>
              <SelectList
                name="rxnt_assign_visit_provider"
                options={assignModalProviders}
                value={assignModalSelected}
                changeHandler={(value) => setAssignModalSelected(value)}
                disabled={assignModalLoading}
                className="form-control"
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-secondary sh_btnsecondary mr-2"
              onClick={handleCancelAssignProvider}
              disabled={assignModalLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary sh_btnprimary"
              onClick={handleSaveAssignProvider}
              disabled={assignModalLoading || !assignModalSelected}
            >
              {assignModalLoading ? 'Saving…' : 'Save'}
            </button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default RxntPrescriptionForm;
